/**
 * Tide Chart
 * Chart.js implementation for 24-hour tide curve
 * Lazy-loads the bundled Chart.js chunk on first use for better TBT
 */

import { formatDateTime } from './utils.js';
import { parseISO, now } from './time.js';
import { analytics } from './analytics.js';

// Chart.js constructor, resolved on first use via dynamic import.
// esbuild code-splits chart-vendor.js (Chart.js + adapter + annotation
// plugin) into a hashed lazy chunk that only loads when a chart renders.
let ChartCtor = null;
let chartLibraryPromise = null;

/**
 * Lazy-load the Chart.js vendor chunk (chart.js + date-fns adapter +
 * annotation plugin, pre-registered in chart-vendor.js)
 */
async function loadChartLibrary() {
    if (ChartCtor) return;
    if (!chartLibraryPromise) {
        chartLibraryPromise = import('./chart-vendor.js').then((mod) => {
            ChartCtor = mod.Chart;
        });
    }
    return chartLibraryPromise;
}

// Global chart instance
let tideChart = null;

// Track if chart interaction has been logged this session
let chartInteractionTracked = false;

// Last rendered data, kept so the chart can re-render on theme change
let lastCurveData = null;
let lastPredictions = null;

// Re-render the current chart with fresh colors when the theme toggles.
// Registered once at module load (module evaluation is idempotent).
window.addEventListener('themechange', () => {
    if (lastCurveData) {
        updateTideChart(lastCurveData, lastPredictions);
    }
});

/**
 * Read a CSS custom property from :root, with a JS fallback string
 */
function cssVar(name, fallback) {
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return value || fallback;
}

/**
 * Resolve chart colors from the design system at chart-build time.
 * Custom property names come from css/_variables.scss (dark) and
 * css/_theme.scss (light-mode overrides); fallbacks mirror the dark theme.
 */
function getThemeColors() {
    return {
        line: cssVar('--color-chart-line', '#00d4aa'),
        fill: cssVar('--color-chart-fill', 'rgba(0, 212, 170, 0.15)'),
        grid: cssVar('--color-chart-grid', 'rgba(148, 184, 200, 0.1)'),
        nowLine: cssVar('--color-chart-now', '#ef5350'),
        tideHigh: cssVar('--color-tide-high', '#00d4aa'),
        tideLow: cssVar('--color-tide-low', '#ffb74d'),
        textPrimary: cssVar('--color-text-primary', '#e8f4f8'),
        textSecondary: cssVar('--color-text-secondary', '#b0d0e0'),
        textMuted: cssVar('--color-text-muted', '#8aaaba'),
        tooltipBg: cssVar('--color-bg-card', '#1a3a5c')
    };
}

/**
 * Update the tide chart with new data
 * Lazy-loads Chart.js on first call
 */
export async function updateTideChart(curveData, predictions) {
    // Lazy-load Chart.js library if not already loaded
    await loadChartLibrary();

    lastCurveData = curveData;
    lastPredictions = predictions;

    const canvas = document.getElementById('tide-chart');
    const ctx = canvas.getContext('2d');
    const colors = getThemeColors();

    if (!curveData || curveData.length === 0) {
        if (tideChart) {
            tideChart.destroy();
            tideChart = null;
        }
        ctx.fillStyle = colors.textMuted;
        ctx.font = '14px -apple-system, BlinkMacSystemFont, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Tide curve data unavailable', canvas.width / 2, canvas.height / 2);
        return;
    }

    const currentTime = now();

    // Create annotations for high/low tide points
    const hiloAnnotations = {};
    if (predictions) {
        predictions.forEach((p, i) => {
            // Timestamp audit (2026-08): /api/get-conditions serves prediction
            // times as ISO 8601 with zone (see parseTideTime note in ui.js), so
            // a plain ISO parse is correct here.
            const pTime = parseISO(p.time);
            if (pTime >= curveData[0].time && pTime <= curveData[curveData.length - 1].time) {
                hiloAnnotations[`hilo${i}`] = {
                    type: 'point',
                    xValue: pTime,
                    yValue: p.value,
                    backgroundColor: p.type === 'H' ? colors.tideHigh : colors.tideLow,
                    borderColor: p.type === 'H' ? colors.tideHigh : colors.tideLow,
                    borderWidth: 2,
                    radius: 6
                };
            }
        });
    }

    const chartConfig = {
        type: 'line',
        data: {
            labels: curveData.map(d => d.time),
            datasets: [{
                label: 'Tide Level',
                data: curveData.map(d => ({ x: d.time, y: d.value })),
                borderColor: colors.line,
                backgroundColor: colors.fill,
                fill: true,
                tension: 0.4,
                pointRadius: 0,
                pointHoverRadius: 5,
                pointHoverBackgroundColor: colors.line,
                borderWidth: 2.5
            }]
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { intersect: false, mode: 'index' },
            plugins: {
                legend: { display: false },
                tooltip: {
                    backgroundColor: colors.tooltipBg,
                    titleColor: colors.textPrimary,
                    bodyColor: colors.textSecondary,
                    borderColor: colors.line,
                    borderWidth: 1,
                    padding: 12,
                    displayColors: false,
                    callbacks: {
                        title: function(context) {
                            // Track first chart interaction (tooltip hover)
                            if (!chartInteractionTracked) {
                                chartInteractionTracked = true;
                                analytics.chartInteraction('tooltip_view', 'tide_curve');
                            }
                            return formatDateTime(parseISO(context[0].parsed.x));
                        },
                        label: function(context) {
                            return `${context.parsed.y.toFixed(2)} ft MLLW`;
                        }
                    }
                },
                annotation: {
                    annotations: {
                        nowLine: {
                            type: 'line',
                            xMin: currentTime,
                            xMax: currentTime,
                            borderColor: colors.nowLine,
                            borderWidth: 2,
                            borderDash: [6, 4],
                            label: {
                                display: true,
                                content: 'Now',
                                position: 'start',
                                backgroundColor: colors.nowLine,
                                color: '#fff',
                                font: { size: 11, weight: 'bold' },
                                padding: 4
                            }
                        },
                        ...hiloAnnotations
                    }
                }
            },
            scales: {
                x: {
                    type: 'time',
                    time: { unit: 'hour', displayFormats: { hour: 'ha' }, tooltipFormat: 'MMM d, h:mm a' },
                    grid: { color: colors.grid, drawBorder: false },
                    ticks: { color: colors.textSecondary, maxTicksLimit: 12, font: { size: 11 } }
                },
                y: {
                    grid: { color: colors.grid, drawBorder: false },
                    ticks: {
                        color: colors.textSecondary,
                        font: { size: 11 },
                        callback: function(value) { return value.toFixed(1) + ' ft'; }
                    }
                }
            }
        }
    };

    if (tideChart) {
        tideChart.data = chartConfig.data;
        tideChart.options = chartConfig.options;
        tideChart.update('none');
    } else {
        tideChart = new ChartCtor(ctx, chartConfig);
    }
}
